import { getTotalPrice } from '@shared/lib/money';
import * as React from 'react';
import { useCallback, useReducer } from 'react';

import { InsuranceProgramFragment, SelectedInsuranceFragment } from '@websky/graphql';

interface ToggleAction {
	type: 'toggle';
	payload: string;
}

type ActionTypes = ToggleAction;

const initialState: string[] = [];

const insuranceReducer = (state: string[] = initialState, action: ActionTypes) => {
	if (action.type === 'toggle') {
		if (state.includes(action.payload)) {
			return state.filter(code => code !== action.payload);
		}

		return [...state, action.payload];
	}

	return state;
};

const createInitialState = (selectedInsurances: SelectedInsuranceFragment[]) => {
	return selectedInsurances.map(insurance => insurance.insuranceProgram.code);
};

export const useInsuranceState = (
	insurances: InsuranceProgramFragment[],
	selectedInsurances: SelectedInsuranceFragment[]
) => {
	const initialState = createInitialState(selectedInsurances);

	const [state, dispatch] = useReducer(insuranceReducer, initialState);

	const confirmedCodes = React.useMemo(() => {
		return state.filter(code =>
			selectedInsurances.some(insurance => !!insurance.policyNumber && insurance.insuranceProgram?.code === code)
		);
	}, [state, selectedInsurances]);

	const selectedWithoutConfirmedCodes = React.useMemo(() => {
		return state.filter(code => !confirmedCodes.includes(code));
	}, [state, confirmedCodes]);

	const totalPrice = React.useMemo(() => {
		const insurancePrograms = insurances.filter(insurance =>
			selectedWithoutConfirmedCodes.includes(insurance.code)
		);

		const totalPrice = getTotalPrice(insurancePrograms, insurance => insurance.totalPrice);

		if (totalPrice.amount > 0) {
			return totalPrice;
		}

		return null;
	}, [insurances, selectedWithoutConfirmedCodes]);

	const hasChanges = React.useMemo(() => {
		return JSON.stringify(initialState.sort()) !== JSON.stringify(state.sort());
	}, [initialState, state]);

	const onToggle = useCallback(
		(code: string) => {
			dispatch({ type: 'toggle', payload: code });
		},
		[state]
	);

	return {
		state,
		dispatch,
		confirmedCodes,
		selectedWithoutConfirmedCodes,
		totalPrice,
		hasChanges,
		onToggle
	};
};
